import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ArrowLeftRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { setCanonical, setPageTitle, setMetaDescription, generateSpeakableSchema } from "@/utils/seo";
import { formatMoney } from "@/utils/taxCalculator";
import { SidebarAds, BottomAdBanner } from "@/components/SponsorBanners";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComparePage() {
  const { cities: citiesParam } = useParams();
  const [allCities, setAllCities] = useState([]);
  const [city1Slug, setCity1Slug] = useState("");
  const [city2Slug, setCity2Slug] = useState("");
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cities`).then(r => {
      setAllCities(r.data);
      if (citiesParam) {
        const parts = citiesParam.split('-vs-');
        if (parts.length === 2) {
          setCity1Slug(parts[0]);
          setCity2Slug(parts[1]);
        }
      }
    });
    setCanonical("/compare");
    setPageTitle("Compare Cost of Living Between US Cities");
    setMetaDescription("Compare cost of living, rent, taxes, and expenses between any two US cities side by side.");
  }, [citiesParam]);

  useEffect(() => {
    if (city1Slug && city2Slug && city1Slug !== city2Slug) {
      setLoading(true);
      axios.get(`${API}/compare/${city1Slug}/${city2Slug}`)
        .then(r => setComparison(r.data))
        .catch(() => setComparison(null))
        .finally(() => setLoading(false));
    }
  }, [city1Slug, city2Slug]);

  const fields = [
    { label: "COL Index", key: "col_index", format: v => v, better: "lower" },
    { label: "1BR Rent", key: "rent_1br", format: formatMoney, better: "lower" },
    { label: "2BR Rent", key: "rent_2br", format: formatMoney, better: "lower" },
    { label: "Home Price", key: "home_price", format: formatMoney, better: "lower" },
    { label: "Groceries", key: "groceries", format: formatMoney, better: "lower" },
    { label: "Transportation", key: "transportation", format: formatMoney, better: "lower" },
    { label: "Utilities", key: "utilities", format: formatMoney, better: "lower" },
    { label: "Healthcare", key: "healthcare", format: formatMoney, better: "lower" },
    { label: "State Tax Rate", key: "state_tax_rate", format: v => `${(v * 100).toFixed(1)}%`, better: "lower" },
    { label: "Median Income", key: "median_household_income", format: formatMoney, better: "higher" },
  ];

  const c1 = comparison?.city1;
  const c2 = comparison?.city2;

  return (
    <div data-testid="compare-page">
      <script type="application/ld+json">{JSON.stringify(generateSpeakableSchema("/compare", ["Compare Cities - Cost of Living"], ["Compare cost of living, rent, taxes, and expenses between any two US cities side by side."]))}</script>

      <section className="hero-pattern py-12 md:py-16">
        <div className="container-main">
          <h1 className="speakable-headline text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-3">
            Compare Cities
          </h1>
          <p className="speakable-summary text-base text-slate-500 mb-8 max-w-lg">
            See how two cities stack up side-by-side on cost of living, rent, taxes, and more.
          </p>

          <div className="flex flex-col md:flex-row items-center gap-4 max-w-2xl">
            <Select value={city1Slug} onValueChange={setCity1Slug}>
              <SelectTrigger className="h-14 text-lg border-slate-200 shadow-sm flex-1" data-testid="compare-city1-select">
                <SelectValue placeholder="Select first city" />
              </SelectTrigger>
              <SelectContent>
                {allCities.map(c => (
                  <SelectItem key={c.slug} value={c.slug}>{c.name}, {c.state_code}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
              <ArrowLeftRight className="w-5 h-5 text-slate-400" />
            </div>

            <Select value={city2Slug} onValueChange={setCity2Slug}>
              <SelectTrigger className="h-14 text-lg border-slate-200 shadow-sm flex-1" data-testid="compare-city2-select">
                <SelectValue placeholder="Select second city" />
              </SelectTrigger>
              <SelectContent>
                {allCities.map(c => (
                  <SelectItem key={c.slug} value={c.slug}>{c.name}, {c.state_code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {loading && <div className="container-main py-12 text-center text-slate-400">Comparing cities...</div>}

      <div className="container-main py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8">
          <div>
            {comparison && c1 && c2 && (
              <div>
                <div className="flex items-center justify-center gap-4 mb-8">
                  <h2 className="text-xl font-bold text-slate-900">{c1.name}, {c1.state_code}</h2>
                  <Badge variant="secondary" className="text-xs">VS</Badge>
                  <h2 className="text-xl font-bold text-slate-900">{c2.name}, {c2.state_code}</h2>
                </div>

                <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                  <table className="w-full text-sm" data-testid="comparison-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-slate-700">Category</th>
                        <th className="text-right px-4 py-3 font-semibold text-emerald-700">{c1.name}</th>
                        <th className="text-right px-4 py-3 font-semibold text-blue-700">{c2.name}</th>
                        <th className="text-center px-4 py-3 font-semibold text-slate-500">Better</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map((f, i) => {
                        const v1 = c1[f.key];
                        const v2 = c2[f.key];
                        let winner = "tie";
                        if (f.better === "lower") winner = v1 < v2 ? "city1" : v1 > v2 ? "city2" : "tie";
                        else winner = v1 > v2 ? "city1" : v1 < v2 ? "city2" : "tie";
                        return (
                          <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                            <td className="px-4 py-3 font-medium text-slate-700">{f.label}</td>
                            <td className={`px-4 py-3 text-right font-mono ${winner === 'city1' ? 'text-emerald-700 font-semibold' : 'text-slate-600'}`}>{f.format(v1)}</td>
                            <td className={`px-4 py-3 text-right font-mono ${winner === 'city2' ? 'text-emerald-700 font-semibold' : 'text-slate-600'}`}>{f.format(v2)}</td>
                            <td className="px-4 py-3 text-center">
                              {winner === "city1" ? <TrendingUp className="w-4 h-4 text-emerald-600 mx-auto" /> :
                               winner === "city2" ? <TrendingDown className="w-4 h-4 text-blue-600 mx-auto" /> :
                               <Minus className="w-4 h-4 text-slate-400 mx-auto" />}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[c1, c2].map((c, i) => {
                    const totalMonthly = c.rent_1br + c.groceries + c.transportation + c.utilities + c.healthcare;
                    return (
                      <Card key={i} className="border-slate-200">
                        <CardContent className="p-5">
                          <h3 className="font-semibold text-slate-900 mb-3">{c.name} Summary</h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-500">Total Monthly Expenses</span>
                              <span className="font-mono font-semibold">{formatMoney(totalMonthly)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Annual Expenses</span>
                              <span className="font-mono font-semibold">{formatMoney(totalMonthly * 12)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Population</span>
                              <span className="font-mono">{c.population?.toLocaleString()}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Apartment Affiliate Banner */}
            <a href="https://www.hellolanding.com/r/web/2vvg1H" target="_blank" rel="noopener" className="no-underline block">
              <div className="p-4 bg-gradient-to-r from-blue-50 via-sky-50 to-blue-50 border border-blue-200 rounded-xl my-6 flex items-center gap-4 hover:shadow-md transition-all" data-testid="post-result-banner">
                <img src="https://images.pexels.com/photos/19899060/pexels-photo-19899060.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop" alt="Furnished apartment" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Sponsored</p>
                  <p className="text-sm font-semibold text-slate-900">Planning a Move? Get $250 Off Your First Furnished Apartment</p>
                  <p className="text-xs text-slate-500">Move freely, stay comfortably. Furnished apartments in 200+ cities. *Limited time offer. Terms apply.</p>
                </div>
                <span className="text-xs font-bold text-blue-600 whitespace-nowrap hidden md:inline">Claim $250 Off &rarr;</span>
              </div>
            </a>

            {!comparison && !loading && (
              <div className="py-16 text-center text-slate-400">
                <p className="text-base">Select two cities above to compare</p>
              </div>
            )}
          </div>

          {/* Sidebar Ads */}
          <div className="hidden lg:block">
            <SidebarAds />
          </div>
        </div>
      </div>

      <BottomAdBanner />
    </div>
  );
}
